import { create } from 'zustand';
import type { AttachedFile } from '../components/ChatArea/types';
import type { FileUploadProcessingResult } from '../../../utils/fileUtils';
import { QueryClient } from '@tanstack/react-query';
import { useWebSocketStore } from '../../../store/websocketStore';
import { TokenUsage } from '../components/ChatArea/TokenUsageDisplay';
import { getClientMetadata } from '../../../utils/browserUtils';
import { prepareMessageWithContext } from '../components/CenteredInput/contextUtils';
import api from '../../../lib/api';
import {
  Conversation as DbConversation,
  conversationKeys,
  stopConversation,
} from '../../../api/conversations';
import { isAxiosError } from 'axios';
import {
  liftConversationStatus,
  resyncUntilSettled,
} from './conversationResync';

// Constants
export const REASONING_MODELS = [
  'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
  'us.anthropic.claude-sonnet-4-5-20250929-v1:0',
];

// Helper functions
export const isReasoningSupported = (modelId: string | null): boolean => {
  return !!modelId && REASONING_MODELS.includes(modelId);
};
export interface ContextUsage {
  percentage: number; // Usage percentage (0-100)
  tokensLeft: number; // Tokens remaining
  maxContextSize: number; // Maximum context window size
  tokensUsed: number; // Tokens used so far
}

// Types
export type StreamingAgentMessageType = (
  | { type: 'start' }
  | {
      type: 'streaming';
      content: {
        type: 'reasoning';
        reasoning: string;
      };
    }
  | {
      type: 'streaming';
      content: ContentBlock;
    }
  | {
      type: 'streaming';
      content: ToolCallChunkContent;
    }
  | {
      type: 'toolResult';
      toolResult: ToolResultBlock[];
    }
  | {
      type: 'end';
      content?: {
        checkpointId?: string;
        contextUsage: ContextUsage;
      };
    }
  | {
      type: 'error';
      content: ErrorBlock;
    }
  | {
      type: 'tokenUsage';
      content: TokenUsage;
    }
  | {
      type: 'conversationCreated';
      content: {
        conversationId: string;
        title?: string;
        createdAt: string;
      };
    }
  | {
      type: 'conversationTitleUpdated';
      content: {
        conversationId: string;
        title: string;
        updatedAt: string;
      };
    }
) & {
  conversationId: string;
  modelName: string;
  messageId: string;
};

export interface TextBlock {
  type: 'text';
  text: string;
  metadata?: {
    contentType?: 'file';
    filename?: string;
    fileType?: string;
    fileSize?: number;
  };
}

export interface ImageBlock {
  type: 'image';
  source:
    | {
        type: 'base64';
        media_type:
          | 'image/jpeg'
          | 'image/png'
          | 'image/gif'
          | 'image/webp'
          | 'image/bmp'
          | 'image/tiff'
          | 'image/svg+xml';
        data: string;
      }
    | {
        type: 'url';
        media_type:
          | 'image/jpeg'
          | 'image/png'
          | 'image/gif'
          | 'image/webp'
          | 'image/bmp'
          | 'image/tiff'
          | 'image/svg+xml';
        url: string;
      };
}

export interface ImageUrlBlock {
  type: 'image_url';
  image_url: string;
}

export interface DocumentBlock {
  type: 'document';
  source:
    | {
        type: 'base64';
        media_type: string; // More flexible type for supported MIME types
        data: string;
      }
    | {
        type: 'url';
        media_type: string; // More flexible type for supported MIME types
        url: string;
      };
  name: string; // Original filename for display
  size: number; // File size in bytes
  metadata?: {
    contentType?: 'file' | 'pdf';
    filename?: string;
    fileType?: string;
    fileSize?: number;
  };
}

// Keep PDFBlock as an alias for backwards compatibility
export type PDFBlock = DocumentBlock;

export interface AskQuestionOption {
  label: string;
  description: string;
  preview?: string;
}

export interface AskQuestionItem {
  question: string;
  header: string;
  options: AskQuestionOption[];
  multiSelect?: boolean;
}

export interface ActiveAskQuestion {
  questions: AskQuestionItem[];
}

export interface ToolUseBlock {
  type: 'toolUse';
  id: string;
  name: string;
  input: {
    [key: string]: unknown;
  };
  meta: unknown;
  tags?: string[];
}

export interface ToolCallChunkContent {
  type: 'toolCallChunk';
  name?: string;
  index: number;
  args?: string;
}

export interface ToolResultBlock {
  type: 'toolResult';
  toolUseId: string;
  toolName: string;
  content?: string | Array<TextBlock | ImageBlock>;
  isError?: boolean;
}

export interface ReasoningBlock {
  type: 'reasoning';
  reasoning: string;
}

export interface ErrorBlock {
  type: 'error' | 'warning';
  title?: string;
  error: string;
}

export interface FileContentBlock {
  type: 'file';
  source_type: 'url';
  url: string;
  metadata?: {
    contentType?: 'file' | 'pdf';
    filename?: string;
    fileType?: string;
    fileSize?: number;
  };
}

export type ContentBlock =
  | TextBlock
  | ImageBlock
  | ImageUrlBlock
  | DocumentBlock
  | FileContentBlock
  | ToolUseBlock
  | ToolCallChunkContent
  | ToolResultBlock
  | ReasoningBlock
  | ErrorBlock;

export type Role = 'user' | 'assistant' | 'system';

/**
 * Message type for chat. selectedToolGroups replaces selectedAgents for extensibility and clarity.
 */
export interface Message {
  id: string;
  role: Role;
  content: ContentBlock[];
  timestamp: number;
  selectedToolGroups?: string[];
  tokenUsage?: TokenUsage;
  checkpointId?: string;
  feedback?: {
    type: 'upvote' | 'downvote';
    message?: string;
  };
  meta?: {
    enableReasoning?: boolean;
    stopped?: boolean;
    [key: string]: unknown;
  };
  startTime?: number; // Time when message processing started
  duration?: number; // Duration in milliseconds
}

/**
 * Plan progress for a single plan (one presentPlan block), keyed by that message's id in planProgressByPlanMessageId.
 * Built from updatePlanProgress tool blocks: completed = checkmarks, inProgress = spinners, skipped = skipped steps, lastNote = latest note.
 */
export interface PlanProgress {
  /** 0-based step indices marked completed (checkmarks in the UI). */
  completedStepIndices: number[];
  /** 0-based step indices currently in progress (spinner; multiple when steps run in parallel). */
  inProgressStepIndices: number[];
  /** 0-based step indices that were skipped (not applicable or intentionally not done). */
  skippedStepIndices: number[];
  /** Latest optional note from updatePlanProgress (e.g. "Created PROJ-123"). */
  lastNote?: string;
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: number;
  updatedAt: number;
  statusUpdatedAt: number;
  isLoading: boolean;
  pendingToolUses: Set<string>;
  toolPanelOpen: boolean;
  focusedViewOpen?: boolean; // New focused view state
  enableReasoning: boolean;
  conversationExternalId?: string;
  hideIncludeContext?: boolean;
  hideToolsBadge?: boolean;
  hideToolUse?: boolean;
  followUpNotify?: boolean;
  contextUsage?: ContextUsage; // Current context usage for the conversation
  spotlightSummaryId?: string; // Spotlight ID if conversation was created from a spotlight
  /** Message id of the in-flight request — used to scope stop signals to the correct message */
  activeMessageId?: string;
  /** True while a stop request has been sent but the backend END event has not yet arrived */
  isStopping?: boolean;
  /** Message id of the most recent presentPlan; updatePlanProgress applies to this plan */
  lastPresentPlanMessageId?: string;
  /** Plan progress per plan message id (for checkbox / in-progress UI) */
  planProgressByPlanMessageId?: Record<string, PlanProgress>;
  meta?: {
    [key: string]: unknown;
  };
  scope: string;
}

// Add context item type for attached context
export interface ContextItem {
  id: string;
  type: string;
  prompt: string;
  attachmentMetaData?: Record<string, unknown>;
  display?: React.ReactNode;
  icon?: React.ReactNode;
}

export interface ChatMetrics {
  eventName: string; // required
  eventType: string; // required
  eventLocation: string; // required
  eventValue?: string | number;
  eventDetails?: unknown;
  timestamp?: string;
}

export interface ChatState {
  conversations: Record<string, Conversation>;
  activeConversationId: string | null;
  loadingConversationId: string | null; // Track which conversation is being loaded
  selectedToolId: string | null;
  focusedViewContent: React.ReactNode | null; // Content to show in focused view
  focusedViewTitle?: string; // Title for the focused view
  focusedViewPrintMode?: 'image' | 'text'; // Print mode for focused view
  activeInputRef: React.RefObject<HTMLTextAreaElement> | null;
  selectedModelId: string | null;
  inputPrompt: string;
  attachedContextItems: ContextItem[];
  chatInformation: React.ReactNode | null;
  isUrlEnabled: boolean;
  isContentEnabled: boolean;
  activeAskQuestion: ActiveAskQuestion | null;
  lastPageContextHash: string | null; // Hash of the last included page context
  pendingMetrics: ChatMetrics | null;
  // Global attachments for the input (persist across new chat navigation within session)
  attachedFiles: AttachedFile[];
  uploadedFiles: FileUploadProcessingResult[];
  activeFileUploadCount: number;
  /**
   * selectedToolGroups holds the ids of currently selected tools for the chat input.
   * This is designed for future extensibility, so we can support multi-tool selection and dynamic tool lists.
   */
  selectedToolGroups: string[];
  setSelectedToolGroups: (toolIds: string[]) => void;
  setPendingMetrics: (metrics: ChatMetrics | null) => void;
  setAttachedFiles: (files: AttachedFile[]) => void;
  addAttachedFiles: (files: AttachedFile[]) => void;
  removeAttachedFile: (id: string) => void;
  clearAttachedFiles: () => void;
  setUploadedFiles: (files: FileUploadProcessingResult[]) => void;
  addUploadedFiles: (files: FileUploadProcessingResult[]) => void;
  removeUploadedFileAt: (index: number) => void;
  clearUploadedFiles: () => void;
  incrementActiveUploads: (count?: number) => void;
  decrementActiveUploads: (count?: number) => void;
  // Actions
  createConversation: (params?: { conversationExternalId?: string }) => string;
  setActiveConversation: ({
    conversationId,
    conversationExternalId,
  }: {
    conversationId?: string;
    conversationExternalId?: string;
  }) => Promise<void>;
  fetchConversationFromDb: (
    conversationId: string,
  ) => Promise<DbConversation | null>;
  updateConversationInState: (
    conversationId: string,
    dbConversation: DbConversation,
  ) => void;
  loadConversationFromDb: (conversationId: string) => Promise<void>;
  /** Poll every conversation still waiting on an `end` event the socket may have lost. */
  resyncLoadingConversations: () => void;
  findConversationIdByExternalId: (
    conversationExternalId: string,
  ) => Promise<{ conversationId: string } | null | undefined>;
  addMessage: (
    conversationId: string,
    message: Omit<Message, 'id' | 'timestamp'>,
  ) => void;
  updateConversationTitle: (conversationId: string, title: string) => void;
  deleteConversation: (conversationId: string) => void;
  handleIncomingMessage: (
    data: StreamingAgentMessageType,
    queryClient: QueryClient,
  ) => void;
  setToolPanelOpen: (conversationId: string, isOpen: boolean) => void;
  setFocusedViewOpen: (conversationId: string, isOpen: boolean) => void;
  setFocusedViewContent: (
    content: React.ReactNode | null,
    title?: string,
    printMode?: 'image' | 'text',
  ) => void;
  setFocusedViewPrintMode: (mode: 'image' | 'text') => void;
  regenerateResponse: (conversationId: string, messageId: string) => void;
  setSelectedToolId: (toolId: string | null) => void;
  setActiveInputRef: (ref: React.RefObject<HTMLTextAreaElement>) => void;
  focusChatInput: () => void;
  toggleReasoningEnabled: (conversationId: string, enabled?: boolean) => void;
  setSelectedModelId: (modelId: string) => void;
  setMessageFeedback: (
    conversationId: string,
    messageId: string,
    feedback: Message['feedback'] | null,
  ) => void;
  stopStreaming: (conversationId: string) => void;
  setInputPrompt: (prompt: string) => void;
  setAttachedContextItems: (items: ContextItem[]) => void;
  addContextItem: (item: ContextItem) => void;
  removeContextItem: (id: string) => void;
  setChatInformation: (information: React.ReactNode | null) => void;
  clearChatInformation: () => void;
  submitInputPrompt: () => Promise<void>;
  setIsUrlEnabled: (enabled: boolean) => void;
  setIsContentEnabled: (enabled: boolean) => void;
  setActiveAskQuestion: (data: ActiveAskQuestion | null) => void;
  resetState: () => void;
  updateConversationState: (
    conversationId: string,
    state: Partial<Conversation>,
  ) => void;
  /** Register a plan (presentPlan) so subsequent updatePlanProgress applies to it */
  setPresentPlan: (conversationId: string, messageId: string) => void;
  /** Merge updatePlanProgress params into the current plan's progress */
  applyPlanProgress: (
    conversationId: string,
    update: {
      completedStepIndices?: number[];
      inProgressStepIndices?: number[];
      skippedStepIndices?: number[];
      note?: string;
    },
  ) => void;
  programmaticOpen: boolean;
  setProgrammaticOpen: (value: boolean) => void;
}

const getProgressiveDisclosureFlag = (): boolean => {
  try {
    // sessionStorage is tab-scoped, so you can have different values in each tab.
    // Set 'pleri_progressive_disclosure' = 'false' to disable (test old behaviour).
    const val = sessionStorage.getItem('pleri_progressive_disclosure');
    if (val === 'false') {
      return false;
    }
  } catch {
    // ignore storage errors
  }
  return true;
};

const getPleriMessageAction = ({
  conversationId,
  messages,
  modelName,
  enableReasoning,
  checkpointId,
  metrics,
  selectedToolGroups,
  conversationExternalId,
  messageId,
}: {
  conversationId: string;
  messages: {
    role: 'user';
    content: ContentBlock[];
    meta?: {
      enableReasoning?: boolean;
      context?: {
        url?: string;
        content?: string;
      };
      [key: string]: unknown;
    };
  }[];
  selectedToolGroups?: string[];
  modelName: string;
  enableReasoning: boolean;
  checkpointId?: string;
  metrics?: ChatMetrics;
  conversationExternalId?: string;
  messageId: string;
}) => {
  const progressiveDisclosure = getProgressiveDisclosureFlag();
  return {
    action: 'PleriAgent',
    messages,
    conversationId,
    conversationExternalId,
    modelName,
    enableReasoning,
    selectedToolGroups,
    checkpointId,
    messageId,
    progressiveDisclosure,
    ...(metrics
      ? {
          eventMetrics: {
            ...metrics,
            timestamp: metrics.timestamp || new Date().toISOString(),
          },
        }
      : {}),
    ...getClientMetadata(),
  };
};

// One poller per conversation; see resyncLoadingConversations.
const activePollers = new Map<string, () => void>();

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: {},
  activeConversationId: null,
  loadingConversationId: null,
  selectedToolId: null,
  focusedViewContent: null,
  focusedViewTitle: undefined,
  focusedViewPrintMode: undefined,
  activeInputRef: null,
  selectedModelId: null,
  inputPrompt: '',
  attachedContextItems: [],
  chatInformation: null,
  isUrlEnabled: false,
  isContentEnabled: false,
  activeAskQuestion: null,
  lastPageContextHash: null,
  pendingMetrics: null,
  attachedFiles: [],
  uploadedFiles: [],
  activeFileUploadCount: 0,
  selectedToolGroups: [], // Default to Plerion selected
  setSelectedToolGroups: (toolIds) => set({ selectedToolGroups: toolIds }),
  setAttachedFiles: (files) => set({ attachedFiles: files }),
  addAttachedFiles: (files) =>
    set((state) => ({ attachedFiles: [...state.attachedFiles, ...files] })),
  removeAttachedFile: (id) =>
    set((state) => ({
      attachedFiles: state.attachedFiles.filter((f) => f.id !== id),
    })),
  clearAttachedFiles: () => set({ attachedFiles: [] }),
  setUploadedFiles: (files) => set({ uploadedFiles: files }),
  addUploadedFiles: (files) =>
    set((state) => ({ uploadedFiles: [...state.uploadedFiles, ...files] })),
  removeUploadedFileAt: (index) =>
    set((state) => ({
      uploadedFiles: state.uploadedFiles.filter((_, i) => i !== index),
    })),
  clearUploadedFiles: () => set({ uploadedFiles: [] }),
  incrementActiveUploads: (count = 1) =>
    set((state) => ({
      activeFileUploadCount: Math.max(0, state.activeFileUploadCount + count),
    })),
  decrementActiveUploads: (count = 1) =>
    set((state) => ({
      activeFileUploadCount: Math.max(0, state.activeFileUploadCount - count),
    })),
  programmaticOpen: false,
  setProgrammaticOpen: (value) => set({ programmaticOpen: value }),

  createConversation: ({
    conversationExternalId,
  }: {
    conversationExternalId?: string;
  } = {}) => {
    const newId = crypto.randomUUID();
    set((state) => ({
      conversations: {
        ...state.conversations,
        [newId]: {
          id: newId,
          title: '',
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          statusUpdatedAt: Date.now(),
          isLoading: false,
          pendingToolUses: new Set(),
          toolPanelOpen: false,
          focusedViewOpen: false,
          enableReasoning: false,
          conversationExternalId,
          scope: 'user',
          meta: {},
        },
      },
      activeConversationId: newId,
      chatInformation: null, // Clear chat information when creating a new conversation
      inputPrompt: '', // Clear input prompt when creating a new conversation
      attachedContextItems: [], // Clear attached context items
      attachedFiles: [], // Clear attached files
      uploadedFiles: [], // Clear uploaded files
      activeFileUploadCount: 0, // Reset file upload count
      isUrlEnabled: false, // Reset URL enabled state
      isContentEnabled: false, // Reset content enabled state
      lastPageContextHash: null, // Reset page context hash
    }));
    return newId;
  },

  setActiveConversation: async ({ conversationId, conversationExternalId }) => {
    const state = get();
    if (conversationExternalId) {
      const conversation = await state.findConversationIdByExternalId(
        conversationExternalId,
      );
      if (!conversation) {
        set({ loadingConversationId: null });
        return;
      }
      conversationId = conversation.conversationId;
    }

    if (!conversationId) {
      return;
    }

    if (state.conversations[conversationId]) {
      // If conversation exists locally, check if it's already loading
      const existingConversation = state.conversations[conversationId];

      // Switch to the conversation immediately
      set({ activeConversationId: conversationId });

      const dbConversation =
        await get().fetchConversationFromDb(conversationId);
      // Only refresh from DB if the conversation is not currently loading
      if (
        !existingConversation.isLoading ||
        dbConversation?.status === 'completed'
      ) {
        try {
          if (dbConversation) {
            // Check if conversation is completed before updating state

            get().updateConversationInState(conversationId, dbConversation);
          }
        } catch (error) {
          console.error('Failed to refresh conversation:', error);
          // Silent failure - user already has cached version
        }
      }
    } else {
      // If conversation doesn't exist locally, show loading and fetch
      set({ loadingConversationId: conversationId });

      try {
        await get().loadConversationFromDb(conversationId);
        set({
          activeConversationId: conversationId,
          loadingConversationId: null,
        });
      } catch (error) {
        console.error('Failed to load conversation:', error);
        set({ loadingConversationId: null });
      }
    }
    get().activeInputRef?.current?.focus();
  },

  fetchConversationFromDb: async (
    conversationId: string,
  ): Promise<DbConversation | null> => {
    try {
      const { data } = await api.get<{ data: DbConversation }>(
        `/api/core/pleri/conversations/${conversationId}`,
      );
      return liftConversationStatus(data.data);
    } catch {
      return null;
    }
  },

  updateConversationInState: (
    conversationId: string,
    dbConversation: DbConversation,
  ) => {
    set((state) => {
      const existingConversation = state.conversations[conversationId];
      // If conversation already exists, merge with fresh data
      if (existingConversation) {
        // If the user stopped streaming, preserve local messages.
        // Overwriting with DB messages would restore full streamed content
        // the user intentionally stopped — regardless of what triggered this call.
        const wasStopped =
          existingConversation.isStopping ||
          existingConversation.messages.at(-1)?.meta?.stopped;

        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...existingConversation,
              messages: wasStopped
                ? existingConversation.messages
                : dbConversation.messages || [],
              updatedAt: new Date(dbConversation.lastUpdated).getTime(),
              isLoading: false,
              contextUsage: dbConversation.contextUsage,
              meta: dbConversation.meta,
              scope: dbConversation.scope,
              // Keep other local state like toolPanelOpen, enableReasoning, etc.
            },
          },
        };
      }

      // If conversation doesn't exist, create new one
      const newConversation: Conversation = {
        id: conversationId,
        title: 'Loaded Conversation', // We'll use a default title since DbConversation doesn't have title
        messages: dbConversation.messages || [],
        createdAt: new Date(dbConversation.createdAt).getTime(),
        updatedAt: new Date(dbConversation.lastUpdated).getTime(),
        statusUpdatedAt: new Date(dbConversation.lastUpdated).getTime(),
        isLoading: false,
        pendingToolUses: new Set(),
        toolPanelOpen: false,
        focusedViewOpen: false,
        enableReasoning: false,
        contextUsage: dbConversation.contextUsage,
        meta: dbConversation.meta,
        scope: dbConversation.scope,
      };

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: newConversation,
        },
      };
    });
  },

  loadConversationFromDb: async (conversationId) => {
    const dbConversation = await get().fetchConversationFromDb(conversationId);
    if (!dbConversation) {
      return;
    }

    const state = get();
    const existingConversation = state.conversations[conversationId];

    // Don't replace messages while the user is actively interacting with an
    // askQuestion panel — the refetch would clear activeAskQuestion and reset
    // all panel state (answers, currentIndex, etc.).
    if (state.activeAskQuestion) {
      return;
    }

    // Check if conversation is completed (not actively loading/streaming)
    const isCompleted =
      dbConversation.status === 'completed' || !existingConversation?.isLoading;

    // Only update state if conversation is completed
    if (isCompleted) {
      get().updateConversationInState(conversationId, dbConversation);
    }
    // If not completed, we keep the local state as it has more recent streaming data
  },

  resyncLoadingConversations: () => {
    const state = get();
    for (const conversation of Object.values(state.conversations)) {
      if (!conversation.isLoading || activePollers.has(conversation.id)) {
        continue;
      }
      const conversationId = conversation.id;
      const cancel = resyncUntilSettled({
        conversationId,
        fetch: (id) => get().fetchConversationFromDb(id),
        apply: (id, dbConversation) =>
          get().updateConversationInState(id, dbConversation),
        stillWaiting: () =>
          get().conversations[conversationId]?.isLoading === true,
        onStop: (id) => activePollers.delete(id),
      });
      activePollers.set(conversationId, cancel);
    }
  },

  findConversationIdByExternalId: async (conversationExternalId: string) => {
    try {
      const { data } = await api.get<{ data: { conversationId: string } }>(
        `/api/core/pleri/conversations/external/${conversationExternalId}`,
      );
      const dbConversation = data.data;
      return dbConversation;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Failed to find conversation id by external id:', error);
    }
  },

  addMessage: (conversationId, message) => {
    const wsStore = useWebSocketStore.getState();
    const metrics = get().pendingMetrics;
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...(state.conversations[conversationId] || {}),
          messages: [
            ...(state.conversations[conversationId]?.messages || []),
            {
              ...message,
              id: crypto.randomUUID(),
              timestamp: Date.now(),
            },
          ],
          updatedAt: Date.now(),
          isLoading: message.role === 'user',
        },
      },
      pendingMetrics: null, // Clear after use
    }));
    if (message.role === 'user') {
      const selectedModelId = get().selectedModelId || '';
      const conversation = get().conversations[conversationId];
      const enableReasoning =
        message.meta?.enableReasoning ||
        (conversation.enableReasoning && isReasoningSupported(selectedModelId));
      const conversationMessageLength = conversation.messages.length;

      // Include spotlightSummaryId in message meta if this is the first message and conversation has a spotlight
      const messageMeta = { ...message.meta };
      if (conversationMessageLength === 0 && conversation.spotlightSummaryId) {
        messageMeta.sourceId = conversation.spotlightSummaryId;
        messageMeta.source = 'spotlight';
      }

      const activeMessageId = crypto.randomUUID();
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            activeMessageId,
          },
        },
      }));

      wsStore.sendMessage(
        getPleriMessageAction({
          conversationId,
          conversationExternalId: conversation.conversationExternalId,
          messages: [
            {
              role: 'user',
              content: message.content,
              meta: messageMeta,
            },
          ],
          selectedToolGroups: message.selectedToolGroups,
          modelName: selectedModelId,
          enableReasoning,
          checkpointId:
            conversationMessageLength === 1 ? crypto.randomUUID() : undefined,
          metrics: metrics ?? undefined,
          messageId: activeMessageId,
        }),
      );
    }
  },

  updateConversationTitle: (conversationId, title) => {
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          title,
          updatedAt: Date.now(),
        },
      },
    }));
  },

  deleteConversation: (conversationId) => {
    set((state) => {
      const { [conversationId]: _, ...remainingConversations } =
        state.conversations;
      return {
        conversations: remainingConversations,
        activeConversationId:
          state.activeConversationId === conversationId
            ? null
            : state.activeConversationId,
      };
    });
  },

  setToolPanelOpen: (conversationId, isOpen) => {
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          toolPanelOpen: isOpen,
        },
      },
    }));
  },

  setFocusedViewOpen: (conversationId, isOpen) => {
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          focusedViewOpen: isOpen,
        },
      },
    }));
  },

  setFocusedViewContent: (content, title, printMode) => {
    set({
      focusedViewContent: content,
      focusedViewTitle: title,
      focusedViewPrintMode: printMode || 'text', // Default to 'text' if not provided
    });
  },
  setFocusedViewPrintMode: (mode) => {
    set({ focusedViewPrintMode: mode });
  },

  handleIncomingMessage: (data, queryClient) => {
    const state = get();
    const activeConversationId = state.activeConversationId;
    if (!activeConversationId || data.conversationId !== activeConversationId) {
      return;
    }

    const conversation = state.conversations[activeConversationId];

    // If the conversation is stopping, block every event type EXCEPT 'end' and 'error'.
    // Using the conversation-level isStopping flag (rather than a per-message id check)
    // fixes the stop-before-first-token race: when no assistant message exists yet,
    // lastMessage is the user message and its id would never match data.messageId.
    // 'end' must still run so it can clear isStopping / isLoading.
    // 'error' must still run so users see backend errors.
    if (
      data.type !== 'end' &&
      data.type !== 'error' &&
      conversation.isStopping
    ) {
      return;
    }

    switch (data.type) {
      case 'start':
        set((state) => ({
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...state.conversations[activeConversationId],
              isLoading: true,
              messages: state.conversations[activeConversationId].messages.map(
                (msg, idx) =>
                  idx ===
                  state.conversations[activeConversationId].messages.length - 1
                    ? { ...msg, startTime: Date.now() }
                    : msg,
              ),
            },
          },
        }));
        break;

      case 'streaming':
        set((state) => {
          const messages = conversation.messages;
          const lastMessage = {
            ...messages[messages.length - 1],
            content: [...(messages[messages.length - 1].content || [])],
          };

          // If the content is a string (raw text), convert it to a TextBlock
          const contentBlock =
            typeof data.content === 'string'
              ? { type: 'text' as const, text: data.content }
              : data.content;

          // If it's a tool use, add it to pending tools, open the panel, and select it
          if (contentBlock.type === 'toolUse') {
            conversation.pendingToolUses?.add(contentBlock.id);

            // Logic to auto open panel. Currently disabled
            if (contentBlock.tags?.includes('pleri:tool:autoExpand')) {
              conversation.toolPanelOpen = true;
              state.selectedToolId = contentBlock.id;
            }
          }

          if (lastMessage.role === 'user') {
            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conversation,
                  messages: [
                    ...messages,
                    {
                      id: data.messageId,
                      content: [contentBlock],
                      role: 'assistant',
                      timestamp: Date.now(),
                      startTime: Date.now(),
                    },
                  ],
                  updatedAt: Date.now(),
                },
              },
            };
          }

          // For text blocks, try to append to the last text block if possible
          if (contentBlock.type === 'text') {
            const lastContent =
              lastMessage.content[lastMessage.content.length - 1];
            if (lastContent?.type === 'text') {
              lastContent.text += contentBlock.text;
            } else {
              lastMessage.content.push(contentBlock);
            }
          } else if (contentBlock.type === 'reasoning') {
            const lastContent =
              lastMessage.content[lastMessage.content.length - 1];
            if (lastContent?.type === 'reasoning') {
              lastContent.reasoning += contentBlock.reasoning;
            } else {
              lastMessage.content.push(contentBlock);
            }
          } else if (contentBlock.type === 'toolCallChunk') {
            // Find existing toolCallChunk with the same name (if name is provided)
            // Or find the last toolCallChunk without a name if no name is provided
            let existingChunkIndex = -1;

            if (contentBlock.name) {
              // Find existing chunk with the same name
              existingChunkIndex = lastMessage.content.findIndex(
                (content) =>
                  content.type === 'toolCallChunk' &&
                  content.name === contentBlock.name,
              );
            } else {
              // If no name provided, find the last toolCallChunk and append to it
              for (let i = lastMessage.content.length - 1; i >= 0; i--) {
                const content = lastMessage.content[i];
                if (content.type === 'toolCallChunk') {
                  existingChunkIndex = i;
                  break;
                }
              }
            }

            if (existingChunkIndex !== -1) {
              const existingChunk = lastMessage.content[
                existingChunkIndex
              ] as ToolCallChunkContent;
              // Append args if provided
              if (contentBlock.args) {
                existingChunk.args =
                  (existingChunk.args || '') + contentBlock.args;
              }
              // Set name only if it's provided (first time only)
              if (contentBlock.name) {
                existingChunk.name = contentBlock.name;
              }
            } else {
              // No existing chunk found, add new one
              lastMessage.content.push(contentBlock);
            }
          } else if (contentBlock.type === 'toolUse') {
            // Check if there's a toolCallChunk with the same name to replace
            const toolCallChunkIndex = lastMessage.content.findIndex(
              (content) =>
                content.type === 'toolCallChunk' &&
                content.name === contentBlock.name,
            );

            if (toolCallChunkIndex !== -1) {
              // Replace the toolCallChunk with the toolUse
              lastMessage.content[toolCallChunkIndex] = contentBlock;
            } else {
              // No existing chunk, just add the toolUse
              lastMessage.content.push(contentBlock);
            }
          } else {
            lastMessage.content.push(contentBlock);
          }

          return {
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...conversation,
                messages: [...messages.slice(0, -1), lastMessage],
                updatedAt: Date.now(),
              },
            },
          };
        });
        break;

      case 'toolResult':
        set((state) => {
          const messages = conversation.messages;
          const lastMessage = messages[messages.length - 1];

          if (lastMessage && lastMessage.role === 'assistant') {
            // Add all tool results to the message content
            for (const result of data.toolResult) {
              lastMessage.content.push(result);
              // Remove the tool from pending when we get its result
              conversation.pendingToolUses.delete(result.toolUseId);
            }

            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conversation,
                  messages: [...messages.slice(0, -1), lastMessage],
                  updatedAt: Date.now(),
                },
              },
            };
          }
          return state;
        });
        break;

      case 'error':
        set((state) => {
          const messages = conversation.messages;

          // Create an error block
          const errorBlock: ErrorBlock = data.content;

          // If there's an assistant message, add the error to it
          if (
            messages.length > 0 &&
            messages[messages.length - 1].role === 'assistant'
          ) {
            const lastMessage = {
              ...messages[messages.length - 1],
              content: [...messages[messages.length - 1].content, errorBlock],
            };

            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conversation,
                  messages: [...messages.slice(0, -1), lastMessage],
                  isLoading: false,
                  updatedAt: Date.now(),
                },
              },
            };
          }
          // Otherwise create a new assistant message with the error
          else {
            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conversation,
                  messages: [
                    ...messages,
                    {
                      id: crypto.randomUUID(),
                      content: [errorBlock],
                      role: 'assistant',
                      timestamp: Date.now(),
                      startTime: Date.now(),
                    },
                  ],
                  isLoading: false,
                  updatedAt: Date.now(),
                },
              },
            };
          }
        });
        break;

      case 'end': {
        // Capture whether this was a user-initiated stop BEFORE the set() clears
        // the flag, so we can skip query invalidation below.
        const currentConv = get().conversations[activeConversationId];
        const wasStopped =
          currentConv?.isStopping ||
          currentConv?.messages.at(-1)?.meta?.stopped;

        set((state) => {
          // Read the CURRENT conversation from state (not the stale closure) so
          // we never overwrite changes that stopStreaming already applied.
          const conv = state.conversations[activeConversationId];
          if (!conv) {
            return state;
          }

          // If the conversation was stopped, just clear the flags — don't
          // recreate the messages array so we avoid a flash/re-render of
          // content the user already sees.
          if (conv.isStopping || conv.messages.at(-1)?.meta?.stopped) {
            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conv,
                  isLoading: false,
                  isStopping: false,
                  contextUsage: data.content?.contextUsage ?? conv.contextUsage,
                },
              },
            };
          }

          const messages = conv.messages;
          if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            const duration = lastMessage.startTime
              ? Date.now() - lastMessage.startTime
              : undefined;

            const updatedLastMessage = {
              ...lastMessage,
              meta: {
                ...lastMessage.meta,
              },
              duration,
              ...(data.content?.checkpointId && {
                checkpointId: data.content.checkpointId,
              }),
            };

            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conv,
                  isLoading: false,
                  isStopping: false,
                  messages: [...messages.slice(0, -1), updatedLastMessage],
                  contextUsage: data.content?.contextUsage,
                },
              },
            };
          }

          // If no messages, just update isLoading and contextUsage
          return {
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...conv,
                isLoading: false,
                isStopping: false,
                contextUsage: data.content?.contextUsage,
              },
            },
          };
        });

        // Only invalidate when the conversation completed normally.
        // A stop-triggered invalidation causes the route's useEffect to re-run
        // (conversationsData changes) → setActiveConversation → fetchConversationFromDb
        // → updateConversationInState overwrites local messages with the DB version,
        // making the full streamed content flash back.
        if (!wasStopped) {
          queryClient.invalidateQueries({ queryKey: conversationKeys.all });
        }
        break;
      }

      case 'tokenUsage':
        set((state) => {
          const messages = conversation.messages;

          // Only update if we have at least one message
          if (messages.length > 0) {
            const lastMessage = {
              ...messages[messages.length - 1],
              tokenUsage: data.content,
            };

            return {
              conversations: {
                ...state.conversations,
                [activeConversationId]: {
                  ...conversation,
                  messages: [...messages.slice(0, -1), lastMessage],
                  updatedAt: Date.now(),
                },
              },
            };
          }
          return state;
        });
        break;

      case 'conversationCreated':
      case 'conversationTitleUpdated':
        // Invalidate conversation list queries when conversations are created or titles updated
        queryClient.invalidateQueries({ queryKey: conversationKeys.all });
        break;
    }
  },

  regenerateResponse: (conversationId: string, messageId: string) => {
    const { conversations } = get();
    if (!conversations[conversationId]) {
      return;
    }

    // Find the message and its index
    const messages = conversations[conversationId]?.messages || [];
    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) {
      return;
    }

    const aiMessage = messages[messageIndex];
    const checkpointId = aiMessage?.checkpointId;

    // Get all messages up to and including the target message
    const messagesToKeep = messages.slice(0, messageIndex);
    // Find the last user message before the target message
    const lastUserMessage = [...messagesToKeep]
      .reverse()
      .find((m) => m.role === 'user');
    if (!lastUserMessage) {
      return;
    }

    // Update the conversation
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          messages: messagesToKeep,
          updatedAt: Date.now(),
          isLoading: true,
        },
      },
    }));

    // Get the selected model ID from the store state
    const selectedModelId = get().selectedModelId || '';
    const conversation = get().conversations[conversationId];
    const enableReasoning =
      lastUserMessage.meta?.enableReasoning ||
      (conversation.enableReasoning && isReasoningSupported(selectedModelId));
    const wsStore = useWebSocketStore.getState();

    const activeMessageId = crypto.randomUUID();
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          activeMessageId,
        },
      },
    }));

    // Send the last user message again to get a new response
    wsStore.sendMessage(
      getPleriMessageAction({
        conversationId,
        conversationExternalId: conversation.conversationExternalId,
        messages: [
          {
            role: 'user',
            content: lastUserMessage.content,
          },
        ],
        modelName: selectedModelId,
        enableReasoning: enableReasoning,
        checkpointId,
        messageId: activeMessageId,
      }),
    );
  },

  setSelectedToolId: (toolId) => {
    set({ selectedToolId: toolId });
  },

  setActiveInputRef: (ref) => {
    set({ activeInputRef: ref });
  },

  focusChatInput: () => {
    const { activeInputRef } = get();
    if (activeInputRef?.current) {
      activeInputRef.current.focus();
    }
  },

  toggleReasoningEnabled: (conversationId: string, enabled?: boolean) => {
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          enableReasoning:
            enabled ?? !state.conversations[conversationId]?.enableReasoning,
        },
      },
    }));
  },

  setSelectedModelId: (modelId: string) => {
    set({ selectedModelId: modelId });
  },

  setMessageFeedback: (conversationId, messageId, feedback) => {
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation) {
        return state;
      }

      const messages = conversation.messages;
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      if (messageIndex === -1) {
        return state;
      }

      const updatedMessage = {
        ...messages[messageIndex],
        feedback: feedback || undefined,
      };

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: [
              ...messages.slice(0, messageIndex),
              updatedMessage,
              ...messages.slice(messageIndex + 1),
            ],
          },
        },
      };
    });
  },

  stopStreaming: (conversationId: string) => {
    const activeMessageId =
      get().conversations[conversationId]?.activeMessageId;
    // Tell the backend to stop the agent (fire and forget)
    stopConversation(conversationId, activeMessageId).catch(() => {
      // Best-effort — frontend still stops locally even if this fails
    });

    set((state) => {
      const conversation = state.conversations[conversationId];
      const messages = conversation.messages;
      const lastMessage = messages.at(-1);

      // If there's already an assistant message being streamed, mark it stopped.
      // If the user clicked stop before any assistant token arrived (last message
      // is still the user message), add a placeholder assistant message so:
      //   1. The isStopping filter in handleIncomingMessage works (already handled
      //      by the conversation-level flag, not per-message id).
      //   2. There is a bubble to show the "Stopped" badge on.
      if (lastMessage?.role === 'assistant') {
        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conversation,
              isLoading: false,
              isStopping: true,
              messages: [
                ...messages.slice(0, -1),
                {
                  ...lastMessage,
                  meta: {
                    ...lastMessage.meta,
                    stopped: true,
                  },
                },
              ],
            },
          },
        };
      }

      // No assistant message yet — append a placeholder stopped bubble
      const placeholderId = activeMessageId ?? crypto.randomUUID();
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            isLoading: false,
            isStopping: true,
            messages: [
              ...messages,
              {
                id: placeholderId,
                role: 'assistant' as const,
                content: [],
                timestamp: Date.now(),
                meta: { stopped: true },
              },
            ],
          },
        },
      };
    });
  },

  setInputPrompt: (prompt) => set({ inputPrompt: prompt }),
  setAttachedContextItems: (items) => set({ attachedContextItems: items }),
  addContextItem: (item) =>
    set((state) => ({
      attachedContextItems: [...state.attachedContextItems, item],
    })),
  removeContextItem: (id) =>
    set((state) => ({
      attachedContextItems: state.attachedContextItems.filter(
        (item) => item.id !== id,
      ),
    })),
  setChatInformation: (information) => set({ chatInformation: information }),
  clearChatInformation: () => set({ chatInformation: null }),
  submitInputPrompt: async () => {
    const state = get();
    const input = state.inputPrompt;
    const attachedContextItems = state.attachedContextItems;
    const selectedToolGroups = state.selectedToolGroups;
    const selectedModelId = state.selectedModelId;
    const conversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    const wsStatus = useWebSocketStore.getState().status;
    const modelSupportsReasoning = isReasoningSupported(selectedModelId);
    const isUrlEnabled = state.isUrlEnabled;
    const isContentEnabled = state.isContentEnabled;
    if (!input.trim() || wsStatus !== 'OPEN') {
      return;
    }
    const { formattedMessage: initialFormattedMessage, newPageContextHash } =
      await prepareMessageWithContext(
        input,
        isUrlEnabled,
        isContentEnabled,
        state.lastPageContextHash,
      );
    let formattedMessage = initialFormattedMessage;
    if (attachedContextItems.length > 0) {
      const contextStart = formattedMessage.indexOf('<context>');
      const contextEnd = formattedMessage.indexOf('</context>');
      const promptsString = attachedContextItems
        .map((item) => item.prompt)
        .join('\n');
      if (contextStart !== -1 && contextEnd !== -1) {
        formattedMessage =
          formattedMessage.slice(0, contextEnd) +
          '\n' +
          promptsString +
          formattedMessage.slice(contextEnd);
      } else {
        formattedMessage += `\n<context>${promptsString}</context>`;
      }
    }
    const options: {
      selectedToolGroups?: string[];
      enableReasoning?: boolean;
    } = {
      selectedToolGroups:
        selectedToolGroups.length > 0 ? selectedToolGroups : undefined,
    };
    if (modelSupportsReasoning && conversation?.enableReasoning) {
      options.enableReasoning = true;
    }
    if (!state.activeConversationId) {
      const newId = get().createConversation();
      if (options?.enableReasoning) {
        get().toggleReasoningEnabled(newId, true);
      }
      get().addMessage(newId, {
        content: [{ type: 'text', text: formattedMessage }],
        role: 'user',
        selectedToolGroups: options?.selectedToolGroups,
      });
    } else {
      get().addMessage(state.activeConversationId, {
        content: [{ type: 'text', text: formattedMessage }],
        role: 'user',
        selectedToolGroups: options?.selectedToolGroups,
      });
    }
    set({
      inputPrompt: '',
      attachedContextItems: [],
      isUrlEnabled: false,
      isContentEnabled: false,
      lastPageContextHash: newPageContextHash,
      attachedFiles: [],
      uploadedFiles: [],
      activeFileUploadCount: 0,
    });
  },
  setIsUrlEnabled: (enabled) => set({ isUrlEnabled: enabled }),
  setIsContentEnabled: (enabled) => set({ isContentEnabled: enabled }),
  setActiveAskQuestion: (data) => set({ activeAskQuestion: data }),

  resetState: () =>
    set({
      conversations: {},
      activeConversationId: null,
      loadingConversationId: null,
      selectedToolId: null,
      activeInputRef: null,
      selectedModelId: null,
      inputPrompt: '',
      attachedContextItems: [],
      chatInformation: null,
      isUrlEnabled: false,
      isContentEnabled: false,
      lastPageContextHash: null,
      programmaticOpen: false,
      attachedFiles: [],
      uploadedFiles: [],
      activeFileUploadCount: 0,
    }),

  setPendingMetrics: (metrics) => set({ pendingMetrics: metrics }),

  updateConversationState: (conversationId, partialConversationState) => {
    set((storeState) => ({
      conversations: {
        ...storeState.conversations,
        [conversationId]: {
          ...storeState.conversations[conversationId],
          ...partialConversationState,
        },
      },
    }));
  },

  /**
   * Registers a plan (presentPlan block) so subsequent updatePlanProgress blocks apply to it.
   * Called by PlanContent when a presentPlan message renders. Sets lastPresentPlanMessageId and
   * initializes planProgressByPlanMessageId[messageId] with empty completed/in-progress arrays.
   */
  setPresentPlan: (conversationId, messageId) => {
    set((storeState) => {
      const conv = storeState.conversations[conversationId];
      if (!conv) {
        return storeState;
      }
      const byId = conv.planProgressByPlanMessageId
        ? { ...conv.planProgressByPlanMessageId }
        : {};
      byId[messageId] = {
        completedStepIndices: [],
        inProgressStepIndices: [],
        skippedStepIndices: [],
        lastNote: undefined,
      };
      return {
        conversations: {
          ...storeState.conversations,
          [conversationId]: {
            ...conv,
            lastPresentPlanMessageId: messageId,
            planProgressByPlanMessageId: byId,
          },
        },
      };
    });
  },

  /**
   * Merges an updatePlanProgress update into the current plan's progress.
   * The "current plan" is the one set by the most recent setPresentPlan (lastPresentPlanMessageId).
   * completedStepIndices and skippedStepIndices are merged (union) with current so both delta updates
   * (old: "just completed [1]") and full cumulative (new: "all completed [0,1,2]") work correctly.
   * inProgressStepIndices is replaced when the update provides a non-empty array; otherwise we keep current.
   * No-op if the conversation has no lastPresentPlanMessageId (e.g. presentPlan not rendered yet).
   */
  applyPlanProgress: (conversationId, update) => {
    set((storeState) => {
      const conv = storeState.conversations[conversationId];
      const planMessageId = conv?.lastPresentPlanMessageId;
      if (!conv || planMessageId === undefined) {
        return storeState;
      }
      const byId = { ...(conv.planProgressByPlanMessageId ?? {}) };
      const current = byId[planMessageId] ?? {
        completedStepIndices: [],
        inProgressStepIndices: [],
        skippedStepIndices: [],
        lastNote: undefined,
      };
      const nextCompleted = Array.isArray(update.completedStepIndices)
        ? Array.from(
            new Set([
              ...current.completedStepIndices,
              ...update.completedStepIndices,
            ]),
          ).sort((a, b) => a - b)
        : current.completedStepIndices;
      const nextSkipped = Array.isArray(update.skippedStepIndices)
        ? Array.from(
            new Set([
              ...current.skippedStepIndices,
              ...update.skippedStepIndices,
            ]),
          ).sort((a, b) => a - b)
        : current.skippedStepIndices;
      const nextInProgress = Array.isArray(update.inProgressStepIndices)
        ? Array.from(new Set(update.inProgressStepIndices)).sort(
            (a, b) => a - b,
          )
        : (current.inProgressStepIndices ?? []);
      const next: PlanProgress = {
        completedStepIndices: nextCompleted,
        inProgressStepIndices: nextInProgress,
        skippedStepIndices: nextSkipped,
        lastNote:
          typeof update.note === 'string' ? update.note : current.lastNote,
      };
      byId[planMessageId] = next;
      return {
        conversations: {
          ...storeState.conversations,
          [conversationId]: {
            ...conv,
            planProgressByPlanMessageId: byId,
          },
        },
      };
    });
  },
}));

// A socket that comes back mid-run will not receive that run's `end` event:
// the agent is still posting to the connection that died. Catch up from the
// database instead.
useWebSocketStore.subscribe((socket, previous) => {
  if (socket.status === 'OPEN' && previous.status !== 'OPEN') {
    useChatStore.getState().resyncLoadingConversations();
  }
});
